import { useState } from 'react';
import { CircleCheck, CircleSlash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PCMP_MIS_DECISION_COPY } from '../../constants/pcmpMis.constants';
import type { PcmpMisDecision } from '../../types/pcmpProposal.types';
import type { PcmpMisNotVerified } from '../../types/pcmpQueue.types';
import { PcmpMisDecisionDialog } from './PcmpMisDecisionDialog';

interface PcmpMisActionButtonsProps {
  proposalId: number;
  refNo: string;
  /** The earlier Not Verified round, shown in the dialog for context. */
  notVerified?: PcmpMisNotVerified | null | undefined;
  className?: string;
}

/**
 * MIS's Verify and Not Verified buttons, each opening the decision dialog.
 * Whether they show is the caller's call (capability or permission).
 */
export function PcmpMisActionButtons({
  proposalId,
  refNo,
  notVerified,
  className,
}: PcmpMisActionButtonsProps) {
  const [open, setOpen] = useState<PcmpMisDecision | null>(null);

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <Button type="button" size="sm" onClick={() => setOpen('VERIFIED')} className="gap-1.5">
        <CircleCheck className="size-3.5" />
        {PCMP_MIS_DECISION_COPY.VERIFIED.label}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setOpen('NOT_VERIFIED')}
        className="gap-1.5 text-destructive hover:text-destructive"
      >
        <CircleSlash className="size-3.5" />
        {PCMP_MIS_DECISION_COPY.NOT_VERIFIED.label}
      </Button>

      {/* Mounted only while open, so each starts empty. */}
      {open ? (
        <PcmpMisDecisionDialog
          target={{ proposalId, refNo, decision: open, notVerified }}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}
